import { CAREER_TIERS, type CareerTierId } from '@crowdfpl/domain/gamification';
import { type ReactElement } from 'react';
import { CAREER_TIER_ICONS } from '@/components/gamification/career-tier-icons';
import {
  CAREER_TIER_ORDER,
  formatCareerTierLabel,
  getCareerTierIndex,
} from '@/components/gamification/career-tier-labels';
import { cn } from '@/helpers/utils';

type TierTone = 'primary' | 'mastermind' | 'gold';

function tierTone(tierId: CareerTierId): TierTone {
  if (tierId === 'legend') {
    return 'gold';
  }
  if (tierId === 'mastermind') {
    return 'mastermind';
  }
  return 'primary';
}

const TONE_CLASSES: Record<
  TierTone,
  { border: string; bg: string; text: string; connector: string }
> = {
  primary: {
    border: 'border-primary',
    bg: 'bg-primary/10',
    text: 'text-primary',
    connector: 'bg-primary',
  },
  mastermind: {
    border: 'border-career-mastermind',
    bg: 'bg-career-mastermind/10',
    text: 'text-career-mastermind',
    connector: 'bg-career-mastermind',
  },
  gold: {
    border: 'border-gold',
    bg: 'bg-gold/10',
    text: 'text-gold',
    connector: 'bg-gold',
  },
};

type CareerTierTimelineProps = {
  currentTier: CareerTierId;
  className?: string;
};

/**
 * Six-node career timeline. Completed/current/locked states come from API `tier`
 * only — never derived from XP on the client.
 */
export function CareerTierTimeline({ currentTier, className }: CareerTierTimelineProps): ReactElement {
  const currentIdx = getCareerTierIndex(currentTier);

  return (
    <div className={cn('relative pt-1', className)} data-testid="career-tier-timeline" aria-label="Career tier timeline">
      <div className="pointer-events-none absolute inset-x-0 top-4 h-0.5" aria-hidden>
        {CAREER_TIER_ORDER.slice(0, -1).map((tierId, index) => {
          const filled = index < currentIdx;
          const tone = TONE_CLASSES[tierTone(tierId)];
          const gapPx = 22;
          return (
            <div
              key={tierId}
              className={cn(
                'absolute h-0.5 motion-reduce:transition-none',
                filled ? tone.connector : 'bg-border',
              )}
              style={{
                left: `calc(${((index + 0.5) / CAREER_TIER_ORDER.length) * 100}% + ${gapPx}px)`,
                right: `calc(${(1 - (index + 1.5) / CAREER_TIER_ORDER.length) * 100}% + ${gapPx}px)`,
              }}
            />
          );
        })}
      </div>

      <ol className="relative grid list-none grid-cols-6 gap-0.5 p-0">
        {CAREER_TIER_ORDER.map((tierId, index) => {
          const Icon = CAREER_TIER_ICONS[tierId];
          const reached = index <= currentIdx;
          const isCurrent = index === currentIdx;
          const tone = TONE_CLASSES[tierTone(tierId)];
          const threshold = CAREER_TIERS[tierId];

          return (
            <li key={tierId} className="flex min-w-0 flex-col items-center">
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 bg-background motion-reduce:transition-none',
                  reached ? cn(tone.border, tone.bg) : 'border-border',
                  isCurrent && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
                )}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <Icon
                  className={cn('h-4 w-4', reached ? tone.text : 'text-muted-foreground opacity-50')}
                  strokeWidth={2.2}
                  aria-hidden
                />
              </div>
              <span
                className={cn(
                  'mt-1.5 whitespace-nowrap text-center text-[10px] leading-tight tracking-tight sm:text-[11px]',
                  isCurrent
                    ? cn('font-bold', tone.text)
                    : reached
                      ? 'font-semibold text-foreground'
                      : 'font-semibold text-muted-foreground/70',
                )}
              >
                {formatCareerTierLabel(tierId)}
              </span>
              <span className="mt-0.5 hidden text-[9px] tabular-nums text-muted-foreground sm:block">
                {threshold.toLocaleString()}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
